import { useCallback } from 'react';
import { records, permissions, providers as providersApi, files } from '../utils/api';
import { trackUxEvent } from '../utils/telemetry';

/** Purpose: Provides record/provider/permission action handlers for the app shell. Inputs: state + deps. Output: memoized action handlers. */
export default function useAppActions({
  recordsList,
  permissionsList,
  providersList,
  setRecordsList,
  setPermissionsList,
  setProvidersList,
  loadData,
  mapRecordType,
  normalizeAccessLevel,
  transformRecord,
  transformPermission,
  showConfirmation,
  closeConfirmation,
  toast,
  sessionStartRef,
  firstUploadTrackedRef
}) {
  const handleAddRecord = useCallback(async (newRecord) => {
    try {
      const { files: filesToUpload, ...recordData } = newRecord;

      const backendRecord = {
        title: recordData.title,
        provider: recordData.provider,
        facility: recordData.facility,
        recordDate: recordData.date,
        recordType: mapRecordType(recordData.type),
        description: recordData.description,
        tags: recordData.tags || []
      };

      const created = await records.create(backendRecord);

      if (filesToUpload && filesToUpload.length > 0) {
        for (const file of filesToUpload) {
          await files.upload(created.id, file);
        }
      }

      if (!firstUploadTrackedRef.current) {
        firstUploadTrackedRef.current = true;
        trackUxEvent('time_to_first_upload', {
          durationMs: Date.now() - sessionStartRef.current,
          source: 'record_import'
        });
      }

      setRecordsList((prev) => [...prev, transformRecord(created)]);
      toast.success('Record added successfully');
      return true;
    } catch (err) {
      console.error('Failed to create record:', err);
      toast.error('Failed to create record');
      return false;
    }
  }, [
    firstUploadTrackedRef,
    mapRecordType,
    sessionStartRef,
    setRecordsList,
    toast,
    transformRecord
  ]);

  const handleDeleteRecord = useCallback((recordId) => {
    const record = recordsList.find((r) => r.id === recordId);

    showConfirmation({
      title: 'Delete Medical Record',
      message: `Are you sure you want to delete "${record?.title}"? This action cannot be undone.`,
      variant: 'danger',
      confirmText: 'Delete Record',
      onConfirm: async () => {
        try {
          await records.delete(recordId);
          setRecordsList((prev) => prev.filter((r) => r.id !== recordId));
          setPermissionsList((prev) => prev.map((p) => ({
            ...p,
            recordIds: p.recordIds.filter((id) => id !== recordId)
          })));
          closeConfirmation();
          toast.success('Record deleted');
        } catch (err) {
          console.error('Failed to delete record:', err);
          toast.error('Failed to delete record');
        }
      }
    });
  }, [closeConfirmation, recordsList, setPermissionsList, setRecordsList, showConfirmation, toast]);

  const handleGrantPermission = useCallback(async (newPermission, refreshOnly = false) => {
    if (refreshOnly || !newPermission) {
      try {
        await loadData();
      } catch (err) {
        console.error('Failed to refresh:', err);
      }
      return;
    }

    if (!newPermission.allRecords && (!newPermission.recordIds || newPermission.recordIds.length === 0)) {
      return;
    }

    try {
      const backendPermission = {
        recordIds: newPermission.recordIds,
        allRecords: Boolean(newPermission.allRecords),
        providerId: newPermission.providerId,
        grantedTo: newPermission.grantedTo,
        organization: newPermission.organization,
        purpose: newPermission.purpose,
        expiryDate: newPermission.expiryDate,
        accessLevel: normalizeAccessLevel(newPermission.accessLevel)
      };

      const created = await permissions.create(backendPermission);
      const transformed = transformPermission(created);
      setPermissionsList((prev) => [...prev, transformed]);
      toast.success('Permission granted');
      return transformed;
    } catch (err) {
      console.error('Failed to grant permission:', err);
      toast.error(err?.message || 'Failed to grant permission');
      throw err;
    }
  }, [loadData, normalizeAccessLevel, setPermissionsList, toast, transformPermission]);

  const handleRevokePermission = useCallback((permissionId) => {
    const permission = permissionsList.find((p) => p.id === permissionId);

    showConfirmation({
      title: 'Revoke HIPAA Authorization',
      message: `Are you sure you want to revoke access for ${permission?.grantedTo} at ${permission?.organization}?`,
      variant: 'warning',
      confirmText: 'Revoke Access',
      onConfirm: async () => {
        try {
          await permissions.revoke(permissionId);
          setPermissionsList((prev) => prev.map((p) => (
            p.id === permissionId ? { ...p, status: 'revoked' } : p
          )));
          closeConfirmation();
          toast.success('Authorization revoked');
        } catch (err) {
          console.error('Failed to revoke permission:', err);
          toast.error('Failed to revoke permission');
        }
      }
    });
  }, [closeConfirmation, permissionsList, setPermissionsList, showConfirmation, toast]);

  const handleAddProvider = useCallback(async (providerInput) => {
    const created = await providersApi.create(providerInput);
    setProvidersList((prev) => [...prev, created]);
    toast.success('Provider added');
    return created;
  }, [setProvidersList, toast]);

  const handleUpdateProvider = useCallback(async (providerId, providerInput) => {
    const updated = await providersApi.update(providerId, providerInput);
    setProvidersList((prev) => prev.map((provider) => (provider.id === providerId ? updated : provider)));
    toast.success('Provider updated');
    return updated;
  }, [setProvidersList, toast]);

  const handleUploadModalSubmit = useCallback(async (newRecord) => {
    let recordInput = { ...newRecord };
    const {
      providerMode,
      selectedProviderId,
      createProvider,
      providerDraft
    } = newRecord;

    if (providerMode === 'existing' && selectedProviderId) {
      const selectedProvider = providersList.find((provider) => provider.id === selectedProviderId);
      if (selectedProvider) {
        recordInput.provider = selectedProvider.name;
        recordInput.facility = selectedProvider.organization;
      }
    }

    if (createProvider && providerDraft?.name && providerDraft?.organization) {
      try {
        const createdProvider = await handleAddProvider(providerDraft);
        recordInput.provider = createdProvider.name;
        recordInput.facility = createdProvider.organization;
      } catch (error) {
        console.warn('Provider creation failed during import; continuing with record upload.', error);
        toast.error('Provider record was not saved; record upload will continue.');
      }
    }

    return handleAddRecord(recordInput);
  }, [handleAddProvider, handleAddRecord, providersList, toast]);

  const handleViewRecord = useCallback((_recordId) => {
    // Intentional no-op: the backend audit log captures record views.
  }, []);

  return {
    handleAddRecord,
    handleDeleteRecord,
    handleGrantPermission,
    handleRevokePermission,
    handleAddProvider,
    handleUpdateProvider,
    handleUploadModalSubmit,
    handleViewRecord
  };
}
